import { Box, Button, styled } from '@mui/material';
import { RefObject, useCallback, useEffect } from 'react';
import { SeasonYearsToFetch } from '../seasonalChartConsts';

const StyledChartBox = styled(Box)(() => ({
  height: '100%',
  width: '100%',
}));

interface SeasonalChartContentProps {
  allLineSeries: Record<string, { label: string; isSelected: boolean; value: string }>;
  setAllLineSeries: React.Dispatch<React.SetStateAction<Record<string, { label: string; isSelected: boolean; value: string }>>>;
  isLoading: boolean;
  hasError: boolean;
  hasNoData: boolean;
  chartContainerRef: RefObject<any>;
  chartLineColorMap: Record<string, string>;
  isShowLineTitles: boolean;
  setIsShowLineTitles: (isShowLineTitles: boolean) => void;
  onShowAllYearsChange: (showAllYears: boolean) => void;
  showAllYears: boolean;
}

export const SeasonalChartContent = ({
  allLineSeries,
  setAllLineSeries,
  isLoading,
  hasError,
  hasNoData,
  chartContainerRef,
  chartLineColorMap,
  isShowLineTitles,
  setIsShowLineTitles,
  onShowAllYearsChange,
  showAllYears,
}: SeasonalChartContentProps) => {
  const toggleSeriesVisibility = useCallback(
    (seriesKey: string) => {
      setAllLineSeries(prev => ({
        ...prev,
        [seriesKey]: {
          ...prev[seriesKey],
          isSelected: !prev[seriesKey].isSelected,
        },
      }));
    },
    [setAllLineSeries]
  );

  const sortedYears = Object.keys(allLineSeries).sort((a, b) => {
    const yearA = parseInt(a.match(/\d+/)?.join('') ?? '0');
    const yearB = parseInt(b.match(/\d+/)?.join('') ?? '0');
    return yearB - yearA;
  });

  const visibleYears = showAllYears ? sortedYears : sortedYears.slice(0, SeasonYearsToFetch);
  const hasMoreYears = sortedYears.length > SeasonYearsToFetch;

  useEffect(() => {
    if (Object.keys(allLineSeries).length > 0) {
      onShowAllYearsChange(false);

      const newAllLineSeries = { ...allLineSeries };
      sortedYears.forEach((key, index) => {
        newAllLineSeries[key] = {
          ...newAllLineSeries[key],
          isSelected: index < SeasonYearsToFetch,
        };
      });
      setAllLineSeries(newAllLineSeries);
    }
  }, []);

  return (
    <>
      <Box display="flex" alignItems="center" marginBottom={1} flexWrap="wrap">
        {visibleYears.map(key => {
          const { value, isSelected, label } = allLineSeries[key];

          return (
            <Box key={key} display="flex" alignItems="center" marginLeft={1} marginBottom={0.5}>
              <input id={key} type="checkbox" checked={isSelected} onChange={() => toggleSeriesVisibility(key)} size={11} />
              <label htmlFor={key} style={{ color: chartLineColorMap[value], marginLeft: 5, fontSize: 11 }}>
                {label}
              </label>
            </Box>
          );
        })}

        {hasMoreYears && (
          <Box marginLeft={1} marginBottom={0.5}>
            <Button
              size="small"
              variant="text"
              onClick={() => onShowAllYearsChange(!showAllYears)}
              sx={{
                fontSize: '11px',
                minWidth: 'auto',
                padding: '2px 8px',
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}
            >
              {showAllYears ? 'Show Less' : `Show ${sortedYears.length - SeasonYearsToFetch} More`}
            </Button>
          </Box>
        )}

        <Box display="flex" alignItems="center" justifyContent="flex-end" flex={1}>
          <input id="showLineTitles" type="checkbox" checked={isShowLineTitles} onChange={() => setIsShowLineTitles(!isShowLineTitles)} size={11} />
          <label htmlFor="showLineTitles" style={{ marginLeft: 5, fontSize: 11 }}>
            {isShowLineTitles ? 'Hide' : 'Show'} axis line labels
          </label>
        </Box>
      </Box>
      {!isLoading ? (
        hasError ? (
          <Box color="error.main" display="flex" flex={1} alignItems="center" justifyContent="center">
            Symbol not found
          </Box>
        ) : !hasNoData ? (
          <StyledChartBox ref={chartContainerRef} />
        ) : (
          <Box display="flex" flex={1} alignItems="center" justifyContent="center">
            No data available
          </Box>
        )
      ) : (
        <Box display="flex" flex={1} alignItems="center" justifyContent="center">
          Loading seasonal chart data...
        </Box>
      )}
    </>
  );
};
